// src/redux/network/friendshipSlice.ts

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../storeTypes";

import {
  searchUsersApi,
  fetchFriendsListApi,
  FriendsListParams,
  fetchSentRequestsApi,
  fetchReceivedRequestsApi,
  sendFriendRequestApi,
  fetchFriendsSuggestionsApi,
  fetchRequestsSuggestionsApi,
  acceptFriendRequestApi,
  declineFriendRequestApi,
  cancelFriendRequestApi,
  deleteFriendshipApi,
  fetchPeopleSuggestionsApi,
} from "../../services/network/friendshipApi";
import type {
  FriendshipStateType,
  FriendshipUserType,
} from "../../types/network/friendshipTypes";
import { logger } from "../../utils/logging/logger";

const initialState: FriendshipStateType = {
  searchResults: [],
  sentRequests: [],
  receivedRequests: [],
  friendsList: [],

  friendsSuggestions: [],
  requestsSuggestions: [],

  peopleSuggestions: [],
  peopleNext: null,
  peoplePage: 1,

  loading: false,
  error: null,
  successMessage: null,

  searchQuery: "",
  searchPage: 1,
  searchNext: null,
  searchTotalCount: 0,
};

// Thunks ------------------------------------------------------------------
export const searchUsers = createAsyncThunk(
  "friendship/searchUsers",
  async (
    { query, page = 1 }: { query: string; page?: number },
    { getState, rejectWithValue },
  ) => {
    try {
      const data = await searchUsersApi(query, page);
      const { results, next, previous, count } = data;

      const { friendsList, sentRequests } = (getState() as RootState)
        .friendship;

      const updatedData = results.map((user: FriendshipUserType) => {
        const sentRequest = sentRequests.find(
          (request: any) => request.to_user.id === user.id,
        );
        const isFriend = friendsList.some(
          (friend: any) => friend.id === user.id,
        );

        return {
          ...user,
        };
      });

      return {
        users: updatedData,
        next,
        previous,
        count,
        page,
        query,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Error searching users.",
      );
    }
  },
);

// Fetch sent friend requests
export const fetchSentRequests = createAsyncThunk(
  "friendship/fetchSentRequests",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchSentRequestsApi();
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Error fetching sent requests.",
      );
    }
  },
);

// Fetch received friend requests
export const fetchReceivedRequests = createAsyncThunk(
  "friendship/fetchReceivedRequests",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchReceivedRequestsApi();
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Error fetching received requests.",
      );
    }
  },
);

// Fetch friends list
export const fetchFriendsList = createAsyncThunk(
  "friendship/fetchFriendsList",
  async (params: FriendsListParams | undefined, { rejectWithValue }) => {
    try {
      const data = await fetchFriendsListApi(params ?? {}); // fallback to {}
      return data?.results ?? data;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.error || "Error fetching friends list.",
      );
    }
  },
);

// Thunk for fetching friend suggestions for Friends tab
export const fetchFriendsSuggestions = createAsyncThunk(
  "friendship/fetchFriendsSuggestions",
  async (limit: number, { rejectWithValue }) => {
    try {
      const data = await fetchFriendsSuggestionsApi(limit);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch friend suggestions.",
      );
    }
  },
);

// Thunk for fetching friend suggestions for Requests tab
export const fetchRequestsSuggestions = createAsyncThunk(
  "friendship/fetchRequestsSuggestions",
  async (limit: number, { rejectWithValue }) => {
    try {
      const data = await fetchRequestsSuggestionsApi(limit);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch request suggestions.",
      );
    }
  },
);

// Fetch people suggestions for Square People
export const fetchPeopleSuggestions = createAsyncThunk(
  "friendship/fetchPeopleSuggestions",
  async (page: number = 1, { rejectWithValue }) => {
    try {
      const data = await fetchPeopleSuggestionsApi(page);
      return { ...data, page };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Error fetching people suggestions.",
      );
    }
  },
);

// Send a friend request
export const sendFriendRequest = createAsyncThunk(
  "friendship/sendFriendRequest",
  async (toUserId: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const data = await sendFriendRequestApi(toUserId);

      const { searchResults = [] } = (getState() as RootState).friendship;

      const updatedSearchResults = searchResults.map((user) =>
        user.id === toUserId
          ? {
              ...user,
              request_sent: true,
              requestSent: true,
              friendship_id: data.id,
              friendshipId: data.id,
            }
          : user,
      );

      dispatch(updateSearchResults(updatedSearchResults));
      dispatch(fetchSentRequests());
      dispatch(fetchReceivedRequests());

      return {
        toUserId,
        friendshipId: data.id,
        message: data.message || "Friend request sent successfully.",
        raw: data,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Error sending friend request.",
      );
    }
  },
);

// export const acceptFriendRequest = createAsyncThunk(
export const acceptFriendRequest = createAsyncThunk(
  "friendship/acceptFriendRequest",
  async (friendshipId: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await acceptFriendRequestApi(friendshipId);
      const friend = response.friend;
      // const { message, friend } = response;

      const { searchResults } = (getState() as RootState).friendship;
      const updatedSearchResults = searchResults.map(
        (user: FriendshipUserType) =>
          user.id === friend.id
            ? { ...user, isFriend: true, requestSent: false }
            : user,
      );

      dispatch(updateSearchResults(updatedSearchResults));
      dispatch(fetchFriendsList());
      dispatch(fetchReceivedRequests());

      return friend;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Error accepting friend request.",
      );
    }
  },
);

// Decline a friend request
export const declineFriendRequest = createAsyncThunk(
  "friendship/declineFriendRequest",
  async (friendshipId: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await declineFriendRequestApi(friendshipId);
      const { message, to_user } = response;
      const { searchResults } = (getState() as RootState).friendship;

      const updatedSearchResults = searchResults.map(
        (user: FriendshipUserType) =>
          user.id === to_user.id ? { ...user, requestSent: false } : user,
      );

      dispatch(updateSearchResults(updatedSearchResults));
      dispatch(fetchReceivedRequests());

      return {
        friendshipId,
        message: message || "Friend request declined successfully.",
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Error declining friend request.",
      );
    }
  },
);

// Thunk to cancel a sent friend request
export const cancelFriendRequest = createAsyncThunk(
  "friendship/cancelFriendRequest",
  async (friendshipId: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await cancelFriendRequestApi(friendshipId);
      const { message } = response;
      const { searchResults } = (getState() as RootState).friendship;

      const updatedSearchResults = searchResults.map((user) =>
        user.friendshipId === friendshipId ? { ...user } : user,
      );
      dispatch(updateSearchResults(updatedSearchResults));

      dispatch(fetchSentRequests());
      return {
        friendshipId,
        message: message || "Friend request canceled successfully.",
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Failed to cancel the friend request.",
      );
    }
  },
);

// Delete a friendship
export const deleteFriendship = createAsyncThunk(
  "friendship/deleteFriendship",
  async (friendshipId: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const data = await deleteFriendshipApi(friendshipId);
      const { searchResults } = (getState() as RootState).friendship;
      const updatedSearchResults = searchResults.map(
        (user: FriendshipUserType) =>
          user.id === friendshipId ? { ...user, isFriend: false } : user,
      );

      dispatch(updateSearchResults(updatedSearchResults));
      dispatch(fetchFriendsList());

      return data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Error deleting friendship.",
      );
    }
  },
);

// Slice ------------------------------------------------------------------
const friendshipSlice = createSlice({
  name: "friendship",
  initialState,
  reducers: {
    clearMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    updateSearchResults(state, action) {
      state.searchResults = action.payload;
    },
    clearPeopleSuggestions(state) {
      state.peopleSuggestions = [];
      state.peopleNext = null;
      state.peoplePage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Search users
      .addCase(searchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        const { users, next, count, page, query } = action.payload;

        state.loading = false;
        state.searchQuery = query;
        state.searchPage = page;
        state.searchNext = next;
        state.searchTotalCount = count;

        // برای صفحه اول، جایگزین کن. برای صفحات بعدی، append کن.
        state.searchResults =
          page === 1 ? users : [...state.searchResults, ...users];
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch sent requests
      .addCase(fetchSentRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSentRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.sentRequests = action.payload;
      })
      .addCase(fetchSentRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch received requests
      .addCase(fetchReceivedRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReceivedRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.receivedRequests = action.payload;
      })
      .addCase(fetchReceivedRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch friends list
      .addCase(fetchFriendsList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFriendsList.fulfilled, (state, action) => {
        state.loading = false;
        state.friendsList = action.payload;
      })
      .addCase(fetchFriendsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Friends Suggestions
      .addCase(fetchFriendsSuggestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFriendsSuggestions.fulfilled, (state, action) => {
        state.loading = false;
        state.friendsSuggestions = action.payload;
      })
      .addCase(fetchFriendsSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Requests Suggestions
      .addCase(fetchRequestsSuggestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequestsSuggestions.fulfilled, (state, action) => {
        state.loading = false;
        state.requestsSuggestions = action.payload;
      })
      .addCase(fetchRequestsSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // People Suggestions
      .addCase(fetchPeopleSuggestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPeopleSuggestions.fulfilled, (state, action) => {
        const { results, next, page } = action.payload;

        state.loading = false;
        state.peopleNext = next;
        state.peoplePage = page;

        if (page === 1) {
          state.peopleSuggestions = results;
        } else {
          state.peopleSuggestions = [...state.peopleSuggestions, ...results];
        }
      })
      .addCase(fetchPeopleSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Send friend request
      .addCase(sendFriendRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(sendFriendRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;

        const toUserId = action.payload.toUserId;

        // Remove from Square People suggestions immediately
        state.peopleSuggestions = state.peopleSuggestions.filter(
          (user) => user.id !== toUserId,
        );

        // Optional: remove from other suggestion lists too
        state.friendsSuggestions = state.friendsSuggestions.filter(
          (user) => user.id !== toUserId,
        );

        state.requestsSuggestions = state.requestsSuggestions.filter(
          (user) => user.id !== toUserId,
        );
      })
      .addCase(sendFriendRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Accept friend request
      .addCase(acceptFriendRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptFriendRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;

        if (action.payload && action.payload.id && action.payload.username) {
          state.friendsList.push(action.payload);
        } else {
          // Keep severity same as before
          logger.error("Invalid friend object received", {
            payload_keys: Object.keys(action.payload ?? {}),
          });
        }
      })
      .addCase(acceptFriendRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Decline friend request
      .addCase(declineFriendRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(declineFriendRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
        state.receivedRequests = state.receivedRequests.filter(
          (request) => request.id !== action.payload.friendshipId,
        );
      })
      .addCase(declineFriendRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Cancel Friend Request
      .addCase(cancelFriendRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(cancelFriendRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
        state.sentRequests = state.sentRequests.filter(
          (request) => request.id !== action.payload.friendshipId,
        );
      })
      .addCase(cancelFriendRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete friendship
      .addCase(deleteFriendship.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFriendship.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
        state.friendsList = state.friendsList.filter(
          (friend) => friend.id !== action.payload.friendshipId,
        );
      })
      .addCase(deleteFriendship.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearMessages,
  clearSearchResults,
  updateSearchResults,
  clearPeopleSuggestions,
} = friendshipSlice.actions;

export default friendshipSlice.reducer;
